import { useFormContext } from 'react-hook-form'
import {
    AddRequesterToHcsRequestIds,
    CodePracticeIds,
    UpdateRequesterForAnHcsRequestBodyIds,
} from '@toktokdoctech/tokapi-client'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DataTableActions from '../../Common/dataTableActions/DataTableActions'
import { DescribeRowParams, GetHeadersParams, CodePracticeFieldOption, RequesterData } from '../_props'
import { DataTableAction } from '../../Common/dataTableActions/_props'
import sxs from '../_sxs'
import { WarningIcon } from '../../../assets/icons/icons'
import DataTableIcon from '../../dataTableIcon/DataTableIcon'

export const REQUESTER_CALLOUT = {
    title: "Un requérant de téléexpertise est un professionnel de santé à l'origine de la demande de prise en charge.",
    text: 'Ses informations sont ensuite tracées sur les documents et sont nécessaires pour la facturation de la téléexpertise.',
}
export const SEARCH_RPPS_TITLE = 'Rechercher un professionnel de santé dans l’annuaire santé'
export const REQUESTER_FORM_TITLE = 'Informations du requérant'
export const GLOBAL_ERROR_MESSAGE = 'Une erreur est survenue. Veuillez réessayer plus tard.'
export const DEFAULT_ERROR_MESSAGE = 'Cette information est obligatoire et n’a pas été renseignée'
export const MISSING_FIELDS_ERROR_MESSAGE =
    'Veuillez corriger tous les champs comportant des erreurs pour pouvoir enregistrer.'
export const FINESS_ERROR_MESSAGE = 'Le numéro FINESS doit être composé de 9 chiffres'
export const AM_ERROR_MESSAGE = 'Le numéro AM doit être composé de 9, 15, 16 ou 17 chiffres'
export const REQUESTER_ALREADY_EXISTS_ERROR_MESSAGE = `Ce requérant est déjà existant dans la liste.\nVous pouvez “annuler” pour quitter la saisie du formulaire.`

export const AM_TOOLTIP_TITLE =
    'Le numéro AM (composé soit de 9, 15, 16 ou 17 chiffres) est accessible dans un document de santé redirigé par ce professionnel, ou en le contactant.'
export const FINESS_TOOLTIP_TITLE =
    "Le numéro FINESS (composé de 9 chiffres) est accessible dans l'annuaire santé, ou dans un document de santé rédigé par ce professionnel."

export const deletionBanner = {
    title: 'Êtes-vous sûr de vouloir supprimer ce requérant ?',
    label: 'Supprimer le requérant',
    subLabel: 'Après confirmation, la suppression sera définitive',
}

export const SECURED_EMAIL_WARNING_HELPER_TEXT =
    'Aucun courriel sécurisé référencé dans l’annuaire santé : ce professionnel ne pourra pas être affecté à une demande de téléexpertise'

export const SCROLL_OFFSET = -300
export const SCROLL_TIMEOUT = 3000

export const useFormContextRequester = useFormContext<RequesterData>

export const codePractices: CodePracticeFieldOption[] = [
    { label: 'Salarié', value: 'Employee' },
    { label: 'Libéral', value: 'Liberal' },
]

export const defaultRppsData: RequesterData = {
    id: '',
    title: null,
    lastName: '',
    firstName: '',
    profession: { code: '', label: '' },
    speciality: { code: '', label: '' },
    rppsNum: '',
    nationalId: '',
    amNum: undefined,
    finessNum: undefined,
    civilityCode: '',
    codePractice: undefined,
    securedEmail: null,
    securedEmailList: null,
}

export const getHeaders = ({ sort, onClick, large }: GetHeadersParams) => {
    const baseHeaders = [
        { label: 'Profession' },
        { label: 'Spécialité' },
        { label: 'Actions', size: 'sm', orientation: 'center' },
    ]
    const largeSpecificHeaders = [
        {
            label: "Nom d'exercice",
            buttonProps: {
                endIcon: sort === 'descending' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />,
                disableElevation: true,
                onClick,
            },
        },
        { label: "Prénom d'exercice" },
    ]
    const smallSpecificHeaders = [{ label: 'Médecin' }]

    const specific = large ? largeSpecificHeaders : smallSpecificHeaders

    return [...specific, ...baseHeaders]
}

export const describeRequesterRow = ({
    row,
    isAdmin,
    large,
    onDelete,
    isRequesterValidationEnabled,
}: DescribeRowParams) => {
    const icon = {
        icon: isRequesterValidationEnabled && !row.hasSecureEmail && (
            <DataTableIcon icon={<WarningIcon />} title="Le courriel sécurisé est requis" />
        ),
    }

    const largeRows = [
        {
            item: row?.lastName?.toUpperCase(),
            ...icon,
        },
        { item: row.firstName },
    ]

    const smallRows = [{ item: `${row?.lastName?.toUpperCase()} ${row?.firstName}`, ...icon }]

    const actionItems: DataTableAction[] = [
        {
            icon: <EditIcon titleAccess="Modifier un requérant" />,
            to: `/requester/${row?.id}`,
        },
    ]

    if (isAdmin && onDelete) {
        actionItems.push({
            icon: <DeleteIcon sx={sxs.requestersTableActionButtonDanger} titleAccess="Supprimer un requérant" />,
            variant: 'danger',
            onClick: () => onDelete(),
        })
    }

    const rows = [
        { item: row?.professionLabel },
        { item: row?.specialityLabel },
        {
            item: <DataTableActions actions={actionItems} />,
            orientation: 'end',
        },
    ]

    return large ? [...largeRows, ...rows] : [...smallRows, ...rows]
}

export const computeSavableRequesterData = (
    data: RequesterData,
    hcsId: string,
): AddRequesterToHcsRequestIds & UpdateRequesterForAnHcsRequestBodyIds => ({
    hcsId,
    rppsNum: data.rppsNum,
    codeProfession: data.profession.code ?? '',
    codeSpeciality: data.speciality?.code || null,
    codePractice: data.codePractice as CodePracticeIds,
    amNum: data.amNum || null,
    finessNum: data.finessNum || null,
    securedEmail: data.securedEmail || null,
})
